import { useCallback, useEffect, useRef, useState } from "react";

export interface TimerData {
  days: string;
  formattedHours: string;
  hours: string;
  minutes: string;
  seconds: string;
  milliseconds: number;
  isPastADay: boolean;
  finished: boolean;
  totalMilliseconds: number;
}

export const useTimer = (
  timerName: string,
  target: string | number,
  on?: { Finish: () => void },
  updateInterval: "second" | "millisecond" = "second"
): TimerData => {
  const [timer, setTimer] = useState<TimerData>({
    days: "00",
    formattedHours: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
    milliseconds: 0,
    isPastADay: false,
    finished: false,
    totalMilliseconds: 0,
  });

  const animationFrameRef = useRef<number>();
  const intervalRef = useRef<NodeJS.Timeout>();
  const hasCompletedRef = useRef<boolean>(false);

  useEffect(() => {
    hasCompletedRef.current = false;
  }, [target]);

  const handleCompletion = useCallback(() => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      if (on?.Finish) {
        on.Finish();
      }
    }
  }, [on?.Finish]);

  const addLeadingZero = (num: number) => (num < 10 ? `0${num}` : num.toString());

  const calculateTimerData = (targetString: string | number): TimerData => {
    const targetDate = new Date(targetString);
    const now = Date.now();
    const targetTime = targetDate.getTime();

    // Handle invalid dates
    if (!targetDate || isNaN(targetTime)) {
      return {
        days: "00",
        formattedHours: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
        milliseconds: 0,
        isPastADay: false,
        finished: true,
        totalMilliseconds: 0,
      };
    }

    const difference = targetTime - now;

    if (difference <= 0) {
      return {
        days: "00",
        formattedHours: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
        milliseconds: 0,
        isPastADay: false,
        finished: true,
        totalMilliseconds: 0,
      };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const allHours = Math.floor(difference / (1000 * 60 * 60));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    const milliseconds = difference % 1000;

    return {
      days: addLeadingZero(days),
      hours: addLeadingZero(allHours),
      formattedHours: addLeadingZero(hours),
      minutes: addLeadingZero(minutes),
      seconds: addLeadingZero(seconds),
      milliseconds,
      isPastADay: days > 0,
      finished: false,
      totalMilliseconds: difference,
    };
  };

  const updateTimerMs = useCallback(() => {
    const current = calculateTimerData(target);

    if (current.finished) {
      setTimer(current);
      handleCompletion();
      return;
    }

    setTimer(prev => {
      const prevCentiseconds = Math.floor(prev.milliseconds / 10);
      const currentCentiseconds = Math.floor(current.milliseconds / 10);

      if (
        prev.days !== current.days ||
        prev.hours !== current.hours ||
        prev.minutes !== current.minutes ||
        prev.seconds !== current.seconds ||
        prevCentiseconds !== currentCentiseconds
      ) {
        return current;
      }
      return prev;
    });

    animationFrameRef.current = requestAnimationFrame(updateTimerMs);
  }, [target, handleCompletion]);

  const updateTimerSec = useCallback(() => {
    const current = calculateTimerData(target);
    setTimer(current);

    if (current.finished) {
      handleCompletion();
    }
  }, [target, handleCompletion]);

  useEffect(() => {
    // Handle null/undefined target
    if (!target) {
      setTimer({
        days: "00",
        formattedHours: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
        milliseconds: 0,
        isPastADay: false,
        finished: true,
        totalMilliseconds: 0,
      });
      return;
    }

    const initial = calculateTimerData(target);
    setTimer(initial);

    if (initial.finished) {
      handleCompletion();
      return;
    }

    if (updateInterval === "millisecond") {
      updateTimerMs();
    } else {
      intervalRef.current = setInterval(updateTimerSec, 1000);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [target, updateInterval]);

  return timer;
};
